import { collection, doc, writeBatch } from 'firebase/firestore';
import { db } from '../lib/firebase/firestore';
import { auth } from '../lib/firebase/auth';
import { COLLECTIONS, AUDIT } from '../config/constants';
import type { AuditLog, DeviceInfo } from '../types/audit';

interface PendingLog {
  action: string;
  category: string;
  resourceType?: string | null;
  resourceId?: string | null;
  actionData?: Record<string, unknown> | null;
  description?: string | null;
  screenName?: string | null;
  functionName?: string | null;
  success?: boolean;
  errorMessage?: string | null;
}

let sessionId: string | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;
const queue: PendingLog[] = [];

function getSessionId(): string {
  if (!sessionId) {
    sessionId = crypto.randomUUID();
  }
  return sessionId;
}

function getDeviceInfo(): DeviceInfo {
  return {
    platform: 'admin_web',
    browser: navigator.userAgent.substring(0, 50),
    userAgent: navigator.userAgent,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

function startAutoFlush(): void {
  if (flushTimer) return;
  flushTimer = setInterval(() => {
    auditService.flush().catch((err) => {
      console.error('[AuditService] Flush failed:', err);
    });
  }, AUDIT.flushIntervalMs);
}

export const auditService = {
  /** @internal Exposed for testing */
  get queue(): PendingLog[] {
    return queue;
  },
  set queue(v: PendingLog[]) {
    queue.length = 0;
    queue.push(...v);
  },

  logAction(
    action: string,
    category: string,
    opts?: {
      resourceType?: string | null;
      resourceId?: string | null;
      actionData?: Record<string, unknown> | null;
      description?: string | null;
      screenName?: string | null;
      functionName?: string | null;
      success?: boolean;
      errorMessage?: string | null;
    },
  ): void {
    queue.push({
      action,
      category,
      resourceType: opts?.resourceType ?? null,
      resourceId: opts?.resourceId ?? null,
      actionData: opts?.actionData ?? null,
      description: opts?.description ?? null,
      screenName: opts?.screenName ?? null,
      functionName: opts?.functionName ?? null,
      success: opts?.success ?? true,
      errorMessage: opts?.errorMessage ?? null,
    });
  },

  logScreenView(screen: string): void {
    auditService.logAction('screen_view', 'navigation', {
      screenName: screen,
      description: `Viewed screen: ${screen}`,
    });
  },

  /** @deprecated Use logAction() directly with a specific category */
  logAdminAction(action: string, description: string): void {
    auditService.logAction(action, 'admin_action', { description });
  },

  async flush(): Promise<void> {
    if (queue.length === 0) return;

    const batch = queue.splice(0, queue.length);
    const user = auth.currentUser;
    const deviceInfo = getDeviceInfo();
    const sid = getSessionId();

    const colRef = collection(db, COLLECTIONS.auditLogs);

    for (let i = 0; i < batch.length; i += AUDIT.batchSize) {
      const chunk = batch.slice(i, i + AUDIT.batchSize);
      const writeBatchInstance = writeBatch(db);

      for (const entry of chunk) {
        const docRef = doc(colRef);
        const log: Omit<AuditLog, 'id'> = {
          userId: user?.uid ?? 'anonymous',
          userEmail: user?.email ?? null,
          timestamp: new Date(),
          action: entry.action,
          category: entry.category,
          resourceType: entry.resourceType ?? null,
          resourceId: entry.resourceId ?? null,
          actionData: entry.actionData ?? null,
          description: entry.description ?? null,
          screenName: entry.screenName ?? null,
          functionName: entry.functionName ?? null,
          sessionId: sid,
          success: entry.success ?? true,
          errorMessage: entry.errorMessage ?? null,
          deviceInfo,
          appVersion: 'admin',
          platform: 'admin_web',
        };
        writeBatchInstance.set(docRef, log);
      }

      await writeBatchInstance.commit();
    }
  },

  dispose(): void {
    if (flushTimer) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
    auditService.flush().catch((err) => {
      console.error('[AuditService] Final flush failed:', err);
    });
  },
};

startAutoFlush();
